<template>
    <generic-page>
        <b-btn
            variant="link"
            size="lg"
            class="pl-0"
            :to="`/creator/creatorVideo/edit/${creatorVideoId}`"
        >
            <font-awesome-icon size="lg" icon="fa-light fa-chevron-left" />
            Back to Video
        </b-btn>

        <div v-if="creatorVideo">
            <div class="d-flex align-items-center justify-content-between mb-3">
                <h5 class="mb-0">Broadcast to Followers</h5>
                <wallet-balance />
            </div>

            <div class="broadcast-header">
                <font-awesome-icon
                    icon="fa-duotone fa-bullhorn"
                    size="2x"
                    class="text-info mb-2"
                />
                <div class="text-secondary small">
                    Send an in-app notification to all your followers about this
                    video. This can only be done once per video.
                </div>
            </div>

            <div class="video-preview">
                <img
                    :src="
                        creatorVideo.previewUrl || creatorVideo.thumbnailUrl
                    "
                    class="video-preview-thumb"
                />
                <div class="video-preview-info">
                    <div
                        v-if="creatorVideo.message"
                        class="text-truncate mb-1"
                    >
                        {{ creatorVideo.message }}
                    </div>
                    <div class="text-secondary small">
                        {{ creatorVideo.publishDateTerse }}
                    </div>
                </div>
            </div>

            <b-alert v-if="hasBroadcasted" variant="success" show class="mt-3">
                <font-awesome-icon
                    icon="fa-light fa-check-circle"
                    class="mr-1"
                />
                This video has already been broadcasted to your followers.
            </b-alert>

            <div v-else>
                <b-alert
                    v-if="followerCount !== null"
                    variant="primary"
                    show
                    class="mt-3"
                >
                    <font-awesome-icon
                        icon="fa-duotone fa-users"
                        class="mr-1"
                    />
                    This will be sent to your
                    <strong>{{ followerCount }}</strong>
                    {{ followerCount === 1 ? "follower" : "followers" }}.
                </b-alert>

                <div class="cost-info">
                    <coin-badge :amount="1" label="Cost" />
                </div>

                <b-alert
                    v-if="error"
                    variant="danger"
                    show
                    class="mt-2"
                >
                    {{ error }}
                </b-alert>

                <b-btn
                    variant="primary"
                    size="lg"
                    pill
                    block
                    class="mt-3"
                    :disabled="balance < 1 || broadcasting"
                    @click="confirmModal = true"
                >
                    <font-awesome-icon
                        icon="fa-duotone fa-bullhorn"
                        class="mr-1"
                    />
                    Broadcast Now
                </b-btn>

                <div
                    v-if="balance < 1"
                    class="text-danger small text-center mt-2"
                >
                    You need at least 1 credit to broadcast.
                </div>
            </div>

            <b-modal
                v-model="confirmModal"
                centered
                title="Confirm Broadcast"
                hide-footer
            >
                <div class="text-center p-2">
                    <font-awesome-icon
                        icon="fa-duotone fa-bullhorn"
                        size="3x"
                        class="text-info mb-3"
                    />
                    <p>
                        Broadcast this video to all your followers? This costs
                        <strong>1 credit</strong> and can only be done once per
                        video.
                    </p>
                    <div class="d-flex gap-2 mt-3">
                        <b-btn
                            variant="light"
                            pill
                            class="flex-fill mr-2"
                            @click="confirmModal = false"
                        >
                            Cancel
                        </b-btn>
                        <b-btn
                            variant="primary"
                            pill
                            class="flex-fill"
                            :disabled="broadcasting"
                            @click="broadcast"
                        >
                            <span v-if="broadcasting">
                                <font-awesome-icon
                                    icon="fa-light fa-spinner-third"
                                    spin
                                    class="mr-1"
                                />
                                Sending...
                            </span>
                            <span v-else>
                                Confirm
                            </span>
                        </b-btn>
                    </div>
                </div>
            </b-modal>
        </div>
        <div v-else>
            <generic-loading />
        </div>
    </generic-page>
</template>

<style scoped>
.broadcast-header {
    text-align: center;
    padding: 20px 16px;
    margin: 8px 0;
    border-radius: 10px;
    background: rgba(23, 162, 184, 0.08);
    border: 1px solid rgba(23, 162, 184, 0.15);
}

.video-preview {
    display: flex;
    gap: 12px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    margin-top: 12px;
}

.video-preview-thumb {
    width: 60px;
    aspect-ratio: 9 / 16;
    object-fit: cover;
    border-radius: 6px;
    flex-shrink: 0;
}

.video-preview-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
}

.cost-info {
    display: flex;
    justify-content: center;
    margin-top: 16px;
}
</style>

<script>
import WalletBalance from "@/components/utility/WalletBalance";

export default {
    components: {
        WalletBalance,
    },
    data() {
        return {
            confirmModal: false,
            broadcasting: false,
            error: null,
        };
    },
    computed: {
        creatorVideoId() {
            return this.$route.params?.id;
        },
        creatorVideo() {
            return this.$store.state.creatorVideo.creatorVideo;
        },
        hasBroadcasted() {
            return this.$store.state.creatorVideo.hasBroadcasted;
        },
        onboardingUser() {
            return this.$store.state.onboarding.user;
        },
        balance() {
            return this.onboardingUser?.wallet?.balance ?? 0;
        },
        followerCount() {
            return this.$store.state.creatorDashboard.creatorUser?.followerCount ?? null;
        },
    },
    created() {
        if (this.creatorVideoId) {
            this.$store.dispatch("creatorVideo/get", {
                creatorVideoId: this.creatorVideoId,
            });
        }
        this.$store.dispatch("creatorDashboard/refresh");
    },
    methods: {
        broadcast() {
            this.broadcasting = true;
            this.error = null;

            this.api
                .post(`/creatorVideo/${this.creatorVideoId}/broadcast`)
                .then(() => {
                    this.confirmModal = false;
                    this.$store.commit("creatorVideo/setHasBroadcasted", true);
                    this.$store.dispatch("onboarding/status", null, {
                        root: true,
                    });
                })
                .catch((err) => {
                    this.confirmModal = false;

                    const errors =
                        err?.response?.data?.errors?.creatorVideo;
                    if (errors?.length) {
                        this.error = errors.join(", ");
                    }
                })
                .finally(() => {
                    this.broadcasting = false;
                });
        },
    },
};
</script>
